import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/vett-pro-landing.css';
import ScoreExplainerModal from '../components/common/ScoreExplainerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VettProLandingPage = () => {
  const [heroScore, setHeroScore] = useState(0);
  const heroPassportRef = useRef(null);
  const scoreAnimated = useRef(false);
  const [showScoreExplainer, setShowScoreExplainer] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  
  // Early Access offer state
  const [earlyAccessData, setEarlyAccessData] = useState({
    currentSignups: 0,
    remainingSlots: 1000,
    isOfferActive: true,
    percentageFilled: 0
  });
  
  // Waitlist count (shown when offer is closed)
  const [waitlistCount, setWaitlistCount] = useState(0);
  
  // VETT ID Lookup state
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  // Animate hero score on mount
  useEffect(() => {
    if (scoreAnimated.current) return;
    scoreAnimated.current = true;
    
    const targetScore = 87;
    const duration = 1500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setHeroScore(Math.round(targetScore * eased));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    // Delay animation start
    setTimeout(() => requestAnimationFrame(animate), 500);
  }, []);

  // Fetch Early Access offer status
  useEffect(() => {
    const fetchEarlyAccessStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/promo/early-access-status`);
        if (response.ok) {
          const data = await response.json();
          setEarlyAccessData({
            currentSignups: data.current_signups,
            remainingSlots: data.remaining_slots,
            isOfferActive: data.is_offer_active,
            percentageFilled: data.percentage_filled
          });
        }
      } catch (error) {
        console.error('Failed to fetch early access status:', error);
        // Keep default values on error
      }
    };
    
    fetchEarlyAccessStatus();
    // Refresh every 60 seconds to keep count updated
    const interval = setInterval(fetchEarlyAccessStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch waitlist count when offer is closed
  useEffect(() => {
    if (earlyAccessData.isOfferActive) return;
    
    const fetchWaitlistCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/promo/waitlist/count`);
        if (response.ok) {
          const data = await response.json();
          setWaitlistCount(data.waitlist_count || 0);
        }
      } catch (error) {
        console.error('Failed to fetch waitlist count:', error);
      }
    };
    
    fetchWaitlistCount();
  }, [earlyAccessData.isOfferActive]);

  // Intersection Observer for fade-up animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.fade-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // FAQ toggle handler
  const handleFaqToggle = (e) => {
    const item = e.currentTarget.closest('.faq-item');
    const wasOpen = item.classList.contains('open');
    
    // Close all FAQ items
    document.querySelectorAll('.faq-item').forEach((i) => i.classList.remove('open'));
    
    // Open clicked item if it was closed
    if (!wasOpen) {
      item.classList.add('open');
    }
  };

  // Waitlist submission handler
  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail || waitlistSubmitting) return;
    
    setWaitlistSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/promo/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail })
      });
      
      if (response.ok) {
        setWaitlistSuccess(true);
        setWaitlistEmail('');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to join waitlist');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
      alert('Failed to join waitlist. Please try again.');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // VETT ID Lookup handler
  const handleLookup = async (idToLookup) => {
    const vettId = idToLookup || lookupId;
    if (!vettId || lookupLoading) return;
    
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/vett-id/${vettId.toUpperCase()}`);
      
      if (response.status === 404) {
        setLookupError('No profile found with this VETT ID');
        return;
      }
      
      if (!response.ok) {
        setLookupError('Failed to lookup profile');
        return;
      }
      
      const data = await response.json();
      
      if (data.status === 'private') {
        setLookupError('This profile is set to private');
        return;
      }
      
      setLookupResult(data);
      setLookupId(vettId.toUpperCase());
    } catch (error) {
      console.error('Lookup error:', error);
      setLookupError('Failed to lookup profile. Please try again.');
    } finally {
      setLookupLoading(false);
    }
  };

  // Handle example VETT ID click
  const handleExampleClick = (vettId) => {
    setLookupId(vettId);
    handleLookup(vettId);
  };

  // Get tier color class
  const getTierClass = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'distinguished': return 't-distinguished';
      case 'established': return 't-established';
      case 'verified': return 't-verified';
      default: return 't-unverified';
    }
  };

  // Get tier badge
  const getTierBadge = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'distinguished': return '✦';
      case 'established': return '●';
      case 'verified': return '✓';
      default: return '○';
    }
  };

  // Calculate score ring offset
  const scoreOffset = 125.6 - (heroScore / 100) * 125.6;

  return (
    <div className="vett-pro-landing">
      {/* Navigation */}
      <nav className="site-nav">
        <Link className="nav-logo" to="/">
          <div className="nav-logo-mark">V</div>
          <span style={{marginLeft: '-2px'}}>ETT Pro</span>
        </Link>
        <div className="nav-links">
          <a href="#how">How it works</a>
          <a href="#score">Your score</a>
          <a href="#verify">Verify a record</a>
          <a href="#faq">FAQ</a>
        </div>
        <div className="nav-ctas">
          <Link className="btn btn-ghost" to="/login" data-testid="nav-signin">
            Sign in
          </Link>
          <Link className="btn btn-primary" to="/signup">
            Claim your VETT ID, free
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="hero-grid"></div>

        <div className="hero-badge mono">
          <span className="badge-dot"></span>
          VERIFIED EMPLOYMENT INFRASTRUCTURE
        </div>

        <h1 className="hero-headline">
          Your career, verified by the people who <span className="accent">watched you do the work.</span>
        </h1>
        <p className="hero-sub">
          VETT Pro gives every professional a permanent, verified career passport. Co-signed by your employers, controlled by you, and recognised by anyone you choose to share it with.
        </p>

        <div className="hero-ctas">
          <Link className="btn-hero-primary" to="/signup" data-testid="hero-cta-signup">
            Get Your VETT ID, Free
          </Link>
          <a className="btn-hero-ghost" href="#how" data-testid="hero-cta-how">
            See How It Works ↓
          </a>
        </div>
        <p className="hero-footnote">Free forever. No credit card. Your VETT ID is issued once and belongs to you for life.</p>

        {/* Passport Demo Card */}
        <div className="passport-demo fade-up" ref={heroPassportRef}>
          <div className="passport-card">
            <svg width="0" height="0">
              <defs>
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1B5AFF" />
                  <stop offset="100%" stopColor="#00C9A7" />
                </linearGradient>
              </defs>
            </svg>
            
            <div className="passport-header">
              <div className="passport-logo mono">VETT PASSPORT</div>
              <div className="passport-id mono">VP-M7K2P</div>
            </div>
            
            <div className="passport-profile">
              <div className="profile-avatar">MR</div>
              <div>
                <div className="profile-name">Maya Rodriguez</div>
                <div className="profile-title">Senior Product Manager, Berlin</div>
              </div>
              <div className="passport-score-block">
                <div className="score-circle">
                  <svg viewBox="0 0 44 44">
                    <circle className="score-bg" cx="22" cy="22" r="20"/>
                    <circle 
                      className="score-fill" 
                      cx="22" cy="22" r="20"
                      style={{ 
                        strokeDashoffset: scoreOffset,
                        stroke: 'url(#scoreGradient)'
                      }}
                    />
                  </svg>
                  <div className="score-label"><span>{heroScore}</span></div>
                </div>
                <div className="tier-badge">DISTINGUISHED</div>
              </div>
            </div>

            <div className="passport-records">
              <div className="record-item">
                <div>
                  <div className="record-company">Stripe</div>
                  <div className="record-role">Lead Product Manager</div>
                  <div className="record-dates">Mar 2021, Jun 2024. 3 yrs 3 mos</div>
                </div>
                <div className="record-right">
                  <div className="record-rating">★★★★★</div>
                  <div className="record-badge badge-verified">VERIFIED</div>
                </div>
              </div>
              <div className="record-item">
                <div>
                  <div className="record-company">Klarna</div>
                  <div className="record-role">Product Manager</div>
                  <div className="record-dates">Jan 2019, Feb 2021. 2 yrs</div>
                </div>
                <div className="record-right">
                  <div className="record-rating">★★★★☆</div>
                  <div className="record-badge badge-verified">VERIFIED</div>
                </div>
              </div>
              <div className="record-item">
                <div>
                  <div className="record-company">Zalando</div>
                  <div className="record-role">Associate PM</div>
                  <div className="record-dates">Jun 2017, Dec 2018. 1 yr 6 mos</div>
                </div>
                <div className="record-right">
                  <div className="record-rating">★★★★☆</div>
                  <div className="record-badge badge-signing">CO-SIGNING</div>
                </div>
              </div>
            </div>

            <div className="passport-footer">
              <div className="verified-by">Verified by <span className="verified-count">2 employers</span></div>
              <div className="passport-domain">vettpro.org/id/VP-M7K2P</div>
            </div>
          </div>
        </div>
      </section>

      {/* Clarity Strip */}
      <div className="strip">
        <div className="strip-inner">
          <div className="strip-item">
            <span className="strip-num mono">01</span>
            <span className="strip-text"><strong>One VETT ID</strong>, issued once, yours for life</span>
          </div>
          <div className="strip-item">
            <span className="strip-num mono">02</span>
            <span className="strip-text"><strong>Co-signed by your employers</strong>, not written by you</span>
          </div>
          <div className="strip-item">
            <span className="strip-num mono">03</span>
            <span className="strip-text"><strong>Verifiable by anyone</strong> you choose to share it with</span>
          </div>
          <div className="strip-item">
            <span className="strip-num mono">04</span>
            <span className="strip-text"><strong>Free</strong>, with no conditions and no expiry date</span>
          </div>
        </div>
      </div>

      {/* Pain Section */}
      <section className="pain-section sec-pad">
        <div className="section-glow" aria-hidden="true">
          <span className="glow-blob glow-blob-blue"></span>
          <span className="glow-blob glow-blob-teal"></span>
        </div>
        <div className="container">
          <div className="section-eyebrow light">Why this exists</div>
          <h2 className="section-title" style={{maxWidth: '760px', marginBottom: '36px'}}>
            You have real experience. The system cannot prove it, and it was never designed to.
          </h2>
          <p className="pain-intro">
            Most applications never reach a human being. They are filtered, scored, and discarded by software built to manage volume, not evaluate people. It cannot tell the difference between ten years of real experience and ten years of fabricated claims. AI has made it trivial to generate a decade of convincing professional history in minutes. Hiring managers know it, and honest professionals are paying the credibility cost of other people's fraud.
          </p>
          <div className="pain-grid">
            <div className="pain-item fade-up">
              <div className="pain-n mono">01 / 04</div>
              <div className="pain-title">When you leave a job, your employer controls what gets said about you.</div>
              <p className="pain-body">They can tell the next person who calls whatever they choose. You are never in the room and never told what was said. There is no record of it, no accountability for it, and no way to correct it. A difficult exit can follow you for years through a channel you are legally prevented from accessing.</p>
            </div>
            <div className="pain-item fade-up">
              <div className="pain-n mono">02 / 04</div>
              <div className="pain-title">A reference call lasts three minutes and delivers almost nothing real.</div>
              <p className="pain-body">Legal teams have coached managers into a single response: confirmed dates, confirmed title, careful silence on everything that matters. The mechanism meant to carry your professional reputation across companies has been emptied of meaning by the same organisations that were supposed to maintain it.</p>
            </div>
            <div className="pain-item fade-up">
              <div className="pain-n mono">03 / 04</div>
              <div className="pain-title">Years of exceptional work leave no verifiable trace when you leave.</div>
              <p className="pain-body">You built something real. You delivered results. You were the person people relied on. Then you handed in your notice, and it was as though none of it happened. Your next employer reads a document you wrote about yourself, trusted at exactly the level it deserves, which is less than what you earned.</p>
            </div>
            <div className="pain-item fade-up">
              <div className="pain-n mono">04 / 04</div>
              <div className="pain-title">You start from zero with every employer, in every country, every time.</div>
              <p className="pain-body">Every application. Every interview. Every new city. Ten years of demonstrated performance, and you are asking a stranger to take a chance on a PDF. The trust you built at your last company does not transfer. It does not compound. It disappears the day you hand in your notice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stats-inner">
          <div className="stat">
            <div className="stat-n mono"><span>78</span>%</div>
            <div className="stat-l">of professionals say a withheld or poor reference cost them an offer they had earned</div>
          </div>
          <div className="stat">
            <div className="stat-n mono">1 in <span>3</span></div>
            <div className="stat-l">professionals has had an employer misrepresent the circumstances of their departure</div>
          </div>
          <div className="stat">
            <div className="stat-n mono"><span>6</span> wks</div>
            <div className="stat-l">average time lost in a hiring process that stalls when references do not hold up</div>
          </div>
          <div className="stat">
            <div className="stat-n mono"><span>0</span></div>
            <div className="stat-l">times you are told what your employer actually said about you on a reference call</div>
          </div>
        </div>
        <p className="stats-note">These are the numbers the hiring system was built to keep invisible.</p>
      </div>

      {/* Leonardo da Vinci Section */}
      <section className="leo-section sec-pad">
        <div className="section-glow" aria-hidden="true">
          <span className="glow-blob glow-blob-blue"></span>
          <span className="glow-blob glow-blob-teal"></span>
        </div>
        <div className="container">
          <div className="leo-layout">
            <div>
              <svg viewBox="0 0 280 370" width="100%" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Illustration of Leonardo da Vinci's 1482 letter of application to Ludovico Sforza">
                <defs>
                  <linearGradient id="pg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#F0E6CC"/>
                    <stop offset="50%" stopColor="#E6D8B0"/>
                    <stop offset="100%" stopColor="#D0C090"/>
                  </linearGradient>
                </defs>
                <rect width="280" height="370" fill="url(#pg)"/>
                <rect x="0" y="0" width="8" height="370" fill="#A89050" opacity=".2"/>
                <rect x="272" y="0" width="8" height="370" fill="#A89050" opacity=".16"/>
                <ellipse cx="230" cy="45" rx="18" ry="12" fill="#907840" opacity=".13"/>
                <ellipse cx="38" cy="320" rx="14" ry="10" fill="#907840" opacity=".1"/>
                <line x1="20" y1="38" x2="260" y2="38" stroke="#806030" strokeWidth=".7" opacity=".5"/>
                <line x1="20" y1="42" x2="260" y2="42" stroke="#806030" strokeWidth=".3" opacity=".3"/>
                <text x="140" y="30" textAnchor="middle" fontFamily="Georgia,serif" fontSize="9" fill="#503A18" opacity=".9" fontStyle="italic">Curriculum Vitae · MCCCCLXXXII</text>
                <text x="140" y="62" textAnchor="middle" fontFamily="Georgia,serif" fontSize="12" fill="#352210" fontWeight="700">Leonardo di ser Piero da Vinci</text>
                <line x1="20" y1="70" x2="260" y2="70" stroke="#806030" strokeWidth=".5" opacity=".4"/>
                <text x="26" y="88" fontFamily="Georgia,serif" fontSize="8" fill="#4A3018" opacity=".85" fontStyle="italic">Al Magnifico Signore Lodovico Sforza</text>
                <text x="26" y="102" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".72">Avendo considerato le prove di tutti quegli</text>
                <text x="26" y="114" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".7">che si reputano maestri et compositori di</text>
                <text x="26" y="126" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".68">strumenti da guerra, et che le inventioni et</text>
                <text x="26" y="138" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".65">operatione non sono niente differenti...</text>
                <text x="26" y="158" fontFamily="Georgia,serif" fontSize="8" fill="#352210" fontWeight="700">Artis et Ingenii</text>
                <line x1="26" y1="162" x2="180" y2="162" stroke="#806030" strokeWidth=".5" opacity=".4"/>
                <text x="26" y="177" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".78">I.    Pontem portatilem habeo</text>
                <text x="26" y="190" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".75">II.   Bombardas, mortaria et passavolanti</text>
                <text x="26" y="203" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".72">III.  Architectura civilis et militaris</text>
                <text x="26" y="216" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".7">IV.  Scultura in marmore et bronzo</text>
                <text x="26" y="229" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".65">V.   Pittura, et alia multa</text>
                <text x="210" y="210" fontFamily="Georgia,serif" fontSize="6.5" fill="#806030" opacity=".58" fontStyle="italic">verificatum?</text>
                <text x="212" y="222" fontFamily="Georgia,serif" fontSize="6.5" fill="#806030" opacity=".45" fontStyle="italic">non constat.</text>
                <text x="26" y="248" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".62">Et ancor de pittura farò quello che si porrà</text>
                <text x="26" y="260" fontFamily="Georgia,serif" fontSize="7.5" fill="#5A4228" opacity=".58">fare si come ogi altro, chiunque si sia...</text>
                <line x1="20" y1="286" x2="260" y2="286" stroke="#806030" strokeWidth=".5" opacity=".3"/>
                <text x="26" y="302" fontFamily="Georgia,serif" fontSize="8" fill="#4A3018" opacity=".78" fontStyle="italic">Leonardo · Milano · MCCCCLXXXII</text>
                <circle cx="238" cy="332" r="16" fill="none" stroke="#806030" strokeWidth=".8" opacity=".28"/>
                <circle cx="238" cy="332" r="11" fill="#907840" opacity=".1"/>
                <text x="238" y="336" textAnchor="middle" fontFamily="Georgia,serif" fontSize="8" fill="#604820" opacity=".42">✦</text>
              </svg>
              <p className="leo-cap">Letter to Ludovico Sforza, 1482. Not one claim verified.</p>
            </div>
            <div className="leo-copy">
              <div className="section-eyebrow">543 years overdue</div>
              <h2 className="section-title" style={{marginBottom: '26px'}}>
                In 1482, Leonardo da Vinci wrote the world's first résumé. Not a single claim was verified. <em>Nothing has changed since.</em>
              </h2>
              <p>He was thirty years old, writing to Ludovico Sforza, Duke of Milan, looking for work. He listed his skills: military engineering, bridge construction, cannon design, architecture, sculpture. Painting came almost as an afterthought, near the bottom of the page.</p>
              <p>The Duke had no way to verify a single word of it. He hired him anyway, because that was the only option available.</p>
              <p>Five hundred and forty three years later, the mechanism is identical. A document you write about yourself. Claims nobody confirms. An employer who decides to take a chance.</p>
              <div className="leo-pullquote">
                <p>"AI has made it trivial to generate a decade of convincing professional experience in minutes. Hiring managers know it. They read every application assuming any of it could be invented, including yours. Honest professionals are paying the price for a fraud problem they did not create."</p>
              </div>
              <p>LinkedIn digitised the résumé. It did not verify it. Reference calls replaced the vouching letter and were coached into careful silence within a generation. The format evolved. The trust problem did not.</p>
              <p>VETT Pro is the first real change to this infrastructure on record: a verified work record, authored by your employer, reviewed and co-signed by you, trusted by everyone who comes after. Not a résumé. Not a recommendation. Not a reference call.</p>
              <p className="leo-close">You are not asking to be trusted anymore. You are arriving pre-verified.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="sec-pad" id="how">
        <div className="container">
          <div className="section-eyebrow">How it works</div>
          <h2 className="section-title" style={{maxWidth: '680px'}}>
            One record, from first click to permanent publish. Every step, timestamped.
          </h2>
          <p className="section-sub" style={{marginBottom: '20px'}}>
            A real sequence, condensed to four days. This is the actual order it happens in, not a marketing summary of it.
          </p>

          <div className="how-timeline">
            <div className="how-tl-item fade-up">
              <div className="how-tl-node-col">
                <div className="how-tl-node">
                  <i className="ti ti-fingerprint"></i>
                </div>
                <div className="how-tl-node-line"></div>
              </div>
              <div className="how-tl-body">
                <div className="how-tl-time mono">DAY 1 · 09:14</div>
                <div className="how-tl-title">You claim your VETT ID</div>
                <p className="how-tl-desc">Free account created. Your permanent credential, in the format VP-XXXXX, is issued the moment your email verifies.</p>
              </div>
            </div>

            <div className="how-tl-item fade-up">
              <div className="how-tl-node-col">
                <div className="how-tl-node">
                  <i className="ti ti-edit"></i>
                </div>
                <div className="how-tl-node-line"></div>
              </div>
              <div className="how-tl-body">
                <div className="how-tl-time mono">DAY 1 · 09:20</div>
                <div className="how-tl-title">You pre-fill your role at the company</div>
                <p className="how-tl-desc">Employer, title, dates, responsibilities, achievements. The record starts with your own account of what happened.</p>
              </div>
            </div>

            <div className="how-tl-item fade-up">
              <div className="how-tl-node-col">
                <div className="how-tl-node">
                  <i className="ti ti-send"></i>
                </div>
                <div className="how-tl-node-line"></div>
              </div>
              <div className="how-tl-body">
                <div className="how-tl-time mono">DAY 1 · 09:22</div>
                <div className="how-tl-title">Invites sent to your manager and HR, simultaneously</div>
                <p className="how-tl-desc">Two secure links, two separate people, dispatched in the same moment. No account or software required on their end.</p>
              </div>
            </div>

            <div className="how-tl-group fade-up">
              <div className="how-tl-group-lbl">
                <i className="ti ti-git-branch"></i>
                Independent, blind submissions. Neither sees the other's input before signing.
              </div>
              <div className="how-tl-group-grid">
                <div className="how-tl-item">
                  <div className="how-tl-node-col">
                    <div className="how-tl-node">
                      <i className="ti ti-briefcase"></i>
                    </div>
                    <div className="how-tl-node-line"></div>
                  </div>
                  <div className="how-tl-body">
                    <div className="how-tl-time mono">DAY 3 · 14:47</div>
                    <div className="how-tl-title">HR confirms dates and title</div>
                    <p className="how-tl-desc">Official employment dates, job title, and departure reason, confirmed independently by HR.</p>
                  </div>
                </div>
                <div className="how-tl-item">
                  <div className="how-tl-node-col">
                    <div className="how-tl-node">
                      <i className="ti ti-pencil"></i>
                    </div>
                    <div className="how-tl-node-line"></div>
                  </div>
                  <div className="how-tl-body">
                    <div className="how-tl-time mono">DAY 3 · 16:02</div>
                    <div className="how-tl-title">Manager submits the performance assessment</div>
                    <p className="how-tl-desc">Rating, strengths, and closing statement, submitted independently by your manager, before either sees the other's input.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="how-tl-item fade-up">
              <div className="how-tl-node-col">
                <div className="how-tl-node">
                  <i className="ti ti-shield-check"></i>
                </div>
                <div className="how-tl-node-line"></div>
              </div>
              <div className="how-tl-body">
                <div className="how-tl-time mono">DAY 3 · 16:05</div>
                <div className="how-tl-title">Automatic conflict check passes</div>
                <p className="how-tl-desc">HR's and your manager's submissions are checked against each other. No discrepancies found between the two.</p>
              </div>
            </div>

            <div className="how-tl-item fade-up">
              <div className="how-tl-node-col">
                <div className="how-tl-node">
                  <i className="ti ti-eye-check"></i>
                </div>
                <div className="how-tl-node-line"></div>
              </div>
              <div className="how-tl-body">
                <div className="how-tl-time mono">DAY 4 · 10:00</div>
                <div className="how-tl-title">You review the combined record and co-sign</div>
                <p className="how-tl-desc">Every field, from both sections, before anything is published. Dispute any specific field, or sign when you are satisfied.</p>
              </div>
            </div>

            <div className="how-tl-item last fade-up">
              <div className="how-tl-node-col">
                <div className="how-tl-node done">
                  <i className="ti ti-lock"></i>
                </div>
              </div>
              <div className="how-tl-body">
                <div className="how-tl-time mono">DAY 4 · 10:01</div>
                <div className="how-tl-title">Record locks and publishes to your VETT ID</div>
                <p className="how-tl-desc">Three signatures. Immutable. Visible to anyone you choose to share your VETT ID with, permanently.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VETT Score Section */}
      <section className="score-section sec-pad" id="score">
        <div className="section-glow" aria-hidden="true">
          <span className="glow-blob glow-blob-blue"></span>
          <span className="glow-blob glow-blob-teal"></span>
        </div>
        <div className="container">
          <div className="section-eyebrow light" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            Your VETT Score
            <button 
              onClick={() => setShowScoreExplainer(true)}
              className="score-help-btn"
              data-testid="score-explainer-trigger"
              aria-label="How VETT Score works"
            >
              ?
            </button>
          </div>
          <h2 className="section-title" style={{maxWidth: '700px'}}>
            A score your employers build for you. <em>Not one you build for yourself.</em>
          </h2>

          <div className="score-layout">
            <div>
              <p className="score-intro">
                Your VETT Score is a number between 0 and 100. Every point comes from a co-signed employer record, not from skills you listed, not from endorsements, not from how carefully you filled in your profile. It is calculated from verified facts that real employers put their names to.
              </p>
              <p className="score-note">
                It grows as your career grows. One thorough co-signed record from a twenty year tenure scores better than six shallow records from six short stints. A professional with one employer their entire career is not penalised, they are recognised. The system rewards depth and honest coverage, not the number of jobs you have held.
              </p>

              <div className="score-label-row">What it reflects</div>
              <div className="score-what">
                <div className="sw-item"><div className="sw-dot"></div>How much of your career history is actually verified, not claimed, verified</div>
                <div className="sw-item"><div className="sw-dot"></div>How your employers rated your performance across the time they worked with you</div>
                <div className="sw-item"><div className="sw-dot"></div>How thoroughly your employment records were completed. A deep record counts for more than a sparse one</div>
                <div className="sw-item"><div className="sw-dot"></div>How verifiable your professional identity is, independent of what you have written about yourself</div>
              </div>

              <div className="score-label-row">Worked examples</div>
              <div className="example-cards">
                <div className="ex-card">
                  <div className="ex-head">
                    <div className="ex-scenario">20 years, one employer, one deep record</div>
                    <div className="ex-score-wrap">
                      <span className="ex-score mono">87</span>
                      <span className="ex-tier-tag" style={{background: 'var(--tier-distinguished-bg)', color: 'var(--tier-distinguished)'}}>Distinguished</span>
                    </div>
                  </div>
                  <p className="ex-body">Complete career coverage. The algorithm does not penalise loyalty, it rewards it.</p>
                </div>
                <div className="ex-card">
                  <div className="ex-head">
                    <div className="ex-scenario">3 years in, one complete record</div>
                    <div className="ex-score-wrap">
                      <span className="ex-score mono">38</span>
                      <span className="ex-tier-tag" style={{background: 'var(--tier-verified-bg)', color: '#6C97FF'}}>Verified</span>
                    </div>
                  </div>
                  <p className="ex-body">100% of career to date is verified. The score grows as the career grows. Exactly right.</p>
                </div>
                <div className="ex-card">
                  <div className="ex-head">
                    <div className="ex-scenario">8 years, two records, full coverage</div>
                    <div className="ex-score-wrap">
                      <span className="ex-score mono">64</span>
                      <span className="ex-tier-tag" style={{background: 'var(--tier-established-bg)', color: 'var(--tier-established)'}}>Established</span>
                    </div>
                  </div>
                  <p className="ex-body">Two employers, both co-signed, no gaps. It reads as honest because it is.</p>
                </div>
                <div className="ex-card">
                  <div className="ex-head">
                    <div className="ex-scenario">10 employers, six sparse records</div>
                    <div className="ex-score-wrap">
                      <span className="ex-score mono">41</span>
                      <span className="ex-tier-tag" style={{background: 'var(--tier-verified-bg)', color: '#6C97FF'}}>Verified</span>
                    </div>
                  </div>
                  <p className="ex-body">Many co-signatures do not make up for incomplete records and coverage gaps.</p>
                </div>
              </div>
            </div>

            <div>
              <div className="score-label-row">Professional tiers</div>
              <div className="score-tiers">
                <div className="tier-card tc-distinguished">
                  <div className="tier-head">
                    <span className="tier-name t-distinguished mono">✦ Distinguished</span>
                    <span className="tier-range t-distinguished mono">75, 100</span>
                  </div>
                  <p className="tier-desc">Exceptional career coverage. Deep, thorough records. Strong performance signal. Career length is irrelevant, thoroughness is everything.</p>
                </div>
                <div className="tier-card tc-established">
                  <div className="tier-head">
                    <span className="tier-name t-established mono">● Established</span>
                    <span className="tier-range t-established mono">50, 74</span>
                  </div>
                  <p className="tier-desc">Strong coverage of your career to date, solid record depth, reliable performance data. A well-verified profile with a history that holds up to scrutiny.</p>
                </div>
                <div className="tier-card tc-verified">
                  <div className="tier-head">
                    <span className="tier-name t-verified mono" style={{color: '#6C97FF'}}>✓ Verified</span>
                    <span className="tier-range t-verified mono" style={{color: '#6C97FF'}}>20, 49</span>
                  </div>
                  <p className="tier-desc">At least one co-signed record. Your identity is partly employer-confirmed. The single most important threshold, crossed with one co-signature regardless of career length.</p>
                </div>
                <div className="tier-card tc-unverified">
                  <div className="tier-head">
                    <span className="tier-name t-unverified mono">Unverified</span>
                    <span className="tier-range t-unverified mono">0, 19</span>
                  </div>
                  <p className="tier-desc">No co-signed records yet. Your VETT ID is live. One record request changes that immediately.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dispute Rights Section */}
      <section className="sec-pad" id="dispute">
        <div className="container">
          <div className="section-eyebrow">Your rights</div>
          <h2 className="section-title" style={{maxWidth: '680px'}}>
            Nothing is published without your signature. That is not a policy. <em>It is how the system is built.</em>
          </h2>

          <div className="dispute-layout">
            <div>
              <p className="dispute-intro">No employer can publish a record about you without your explicit co-signature. This is a structural constraint, not a setting that can be toggled and not a promise in a terms document. The system requires your acknowledgement before any record reaches your passport. If what an employer has written is inaccurate, you dispute it: formally, in writing, on permanent record.</p>
              <div className="dispute-steps">
                <div className="ds fade-up"><div className="ds-n mono">01</div><div><div className="ds-title">You review every field before anything is visible</div><div className="ds-body">Role, dates, departure reason, performance rating, strengths, closing statement. Every word, before it goes anywhere.</div></div></div>
                <div className="ds fade-up"><div className="ds-n mono">02</div><div><div className="ds-title">You select exactly what you are contesting</div><div className="ds-body">Field-level dispute selection. You mark each specific claim you believe is wrong and provide your written account of what actually happened.</div></div></div>
                <div className="ds fade-up"><div className="ds-n mono">03</div><div><div className="ds-title">The employer has 14 days to respond</div><div className="ds-body">If they do not respond, a VETT administrator makes an independent determination based on your submission alone. Silence is never treated as agreement with the employer.</div></div></div>
                <div className="ds fade-up"><div className="ds-n mono">04</div><div><div className="ds-title">The outcome is permanent, on both sides</div><div className="ds-body">If the dispute is upheld, the employer corrects the record before publication. If not, your written account is permanently attached to the record and visible to every employer who views it. You are never simply overruled.</div></div></div>
              </div>
            </div>
            <div className="rights-grid">
              <div className="right-card hl fade-up">
                <div className="rc-title">You cannot be compelled to co-sign</div>
                <p className="rc-body">A record cannot be published without your signature. You can decline, and it will not appear on your passport.</p>
              </div>
              <div className="right-card fade-up">
                <div className="rc-title">Your account stays attached permanently</div>
                <p className="rc-body">Even where a dispute is not upheld, your written account of events stays permanently visible to every employer who views that record.</p>
              </div>
              <div className="right-card fade-up">
                <div className="rc-title">Post-publication disputes exist</div>
                <p className="rc-body">If you discover an inaccuracy after publication, you can open a dispute. Upheld disputes result in the record being archived from view.</p>
              </div>
              <div className="right-card fade-up">
                <div className="rc-title">You decide who sees your passport</div>
                <p className="rc-body">Full visibility, selective access that requires your approval, or fully private. Block specific organisations. Settings apply immediately.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Portability Section */}
      <section className="global-section sec-pad">
        <div className="section-glow" aria-hidden="true">
          <span className="glow-blob glow-blob-blue"></span>
          <span className="glow-blob glow-blob-teal"></span>
        </div>
        <div className="container">
          <div className="section-eyebrow light">Works everywhere</div>
          <h2 className="section-title" style={{maxWidth: '780px', marginBottom: '56px'}}>
            One record. Every country. No more reference calls. <em>No more retyping your history for every application you send.</em>
          </h2>
          <div className="global-grid">
            <div className="gl-item fade-up">
              <div className="gl-title">Verifiable by anyone you share your VETT ID with</div>
              <p className="gl-body">Any employer you share your VETT ID with can verify your record instantly. No account required on their end, no login, no friction. A recruiter in Tokyo, a hiring manager in Lagos, an HR director in São Paulo. Seconds, not days.</p>
            </div>
            <div className="gl-item fade-up">
              <div className="gl-title">No more retyping your employment history</div>
              <p className="gl-body">Your VETT Passport is the permanent, structured version of your career. Share your VETT ID, the employer looks it up, and the conversation begins from verified facts rather than a document you formatted the night before.</p>
            </div>
            <div className="gl-item fade-up">
              <div className="gl-title">No more reference calls</div>
              <p className="gl-body">A co-signed VETT record delivers everything a reference call was supposed to: honestly, in writing, permanently. No coached silence, no "we can only confirm the dates," no three minutes of managed nothing.</p>
            </div>
            <div className="gl-item fade-up">
              <div className="gl-title">Your past employer needs nothing but a work email</div>
              <p className="gl-body">They receive a secure link and complete the assessment in their browser. No account, no software, no procurement process. Under ten minutes from any device.</p>
            </div>
            <div className="gl-item fade-up">
              <div className="gl-title">Built for every jurisdiction</div>
              <p className="gl-body">GDPR, FCRA, and data protection frameworks across Europe, North America, Africa, and Asia Pacific are all supported. Where employees have a legal right to a formal employment reference, VETT Pro's request process is built to align with that entitlement.</p>
            </div>
            <div className="gl-item fade-up">
              <div className="gl-title">Portable for the full length of your career</div>
              <p className="gl-body">Your VETT ID is permanent. Every record adds to one continuous, verified professional history. It does not reset when you change companies, does not expire when you change countries, and does not belong to any employer you worked for.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="compare-section sec-pad">
        <div className="section-glow" aria-hidden="true">
          <span className="glow-blob glow-blob-blue"></span>
          <span className="glow-blob glow-blob-teal"></span>
        </div>
        <div className="container">
          <div className="compare-divider"></div>
          <div className="section-eyebrow light">The comparison</div>
          <h2 className="section-title" style={{maxWidth: '720px', marginBottom: '20px'}}>
            Every tool the hiring system gave you was built for the employer. <em>None of them were built to protect what you built.</em>
          </h2>
          <p className="compare-intro">Eight mechanisms. What the old way does with each one, and what VETT Pro replaces it with.</p>
          <div className="compare-table-wrap">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>The mechanism</th>
                  <th>The VETT way</th>
                  <th>The old way</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>How your work history is recorded</td>
                  <td>Your employer authors it, you review every field, both co-sign. Once locked, neither side can change a word.</td>
                  <td>You write it. No one confirms it. Every employer reading it is taking your word for every line.</td>
                </tr>
                <tr>
                  <td>What happens when you leave a company</td>
                  <td>Every co-signed record stays on your passport permanently. Leaving does not erase what you built there.</td>
                  <td>Your performance record disappears. The reputation you built travels with you only as a claim.</td>
                </tr>
                <tr>
                  <td>How references work</td>
                  <td>The record is already there, already signed, already permanent. There is no call to make and nothing left to withhold.</td>
                  <td>A future employer calls a past one. You are not in the room, you do not know what is said, and you have no right to know.</td>
                </tr>
                <tr>
                  <td>What an employer sees before meeting you</td>
                  <td>A co-signed record from someone who managed you, rated your performance, and put their name on it. A verified fact.</td>
                  <td>A document you formatted and keywords you selected, with no independent confirmation that any of it is accurate.</td>
                </tr>
                <tr>
                  <td>What AI and fraud do to your application</td>
                  <td>Co-signed records cannot be fabricated. They require a real employer on the other side, structurally resistant to the fraud collapsing trust everywhere else.</td>
                  <td>AI-generated CVs are indistinguishable from real ones. You pay the credibility cost of other people's fraud.</td>
                </tr>
                <tr>
                  <td>What crossing a border does to your career</td>
                  <td>Your VETT Passport crosses with you. Same verified records, same score, recognised by any employer you share your VETT ID with, in any country.</td>
                  <td>Your professional reputation does not transfer. A new country means explaining companies no one has heard of and rebuilding credibility from scratch.</td>
                </tr>
                <tr>
                  <td>What a gap, redundancy, or difficult exit does</td>
                  <td>Every other verified record speaks independently. A single difficult departure does not define a career that is otherwise verified. Your dispute rights exist precisely for this.</td>
                  <td>It creates a silence that every future employer interprets in the worst possible way, with no mechanism for you to correct the narrative.</td>
                </tr>
                <tr>
                  <td>Who owns your professional reputation</td>
                  <td>You do. Your VETT ID cannot be claimed by anyone else and your passport cannot be altered by any employer. It belongs to you for life.</td>
                  <td>Your employers do. They hold the references, control the narrative, and decide what gets said and to whom. You have no right of reply.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="compare-pull">The old system was not designed badly. It was designed for a different era. The era ended, and the system did not notice. VETT Pro is the correction.</p>
        </div>
      </section>

      {/* Live VETT ID Lookup Section */}
      <section className="lookup-section sec-pad" id="verify">
        <div className="container">
          <div className="section-eyebrow">Live verification</div>
          <h2 className="section-title" style={{marginBottom: '10px'}}>Enter a VETT ID. See exactly what an employer sees.</h2>
          <p className="section-sub">This is what verification looks like in practice. No login required, no account.</p>

          <div className="lookup-layout">
            <div>
              <div className="lc-label" style={{marginTop: '36px'}}>VETT ID</div>
              <div className="lc-row">
                <input 
                  className="lc-inp mono" 
                  placeholder="e.g. VP-M7K2P" 
                  maxLength="10" 
                  aria-label="VETT ID lookup" 
                  data-testid="vett-id-lookup-input"
                  value={lookupId}
                  onChange={(e) => setLookupId(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
                />
                <button 
                  className="lc-btn" 
                  type="button" 
                  data-testid="vett-id-lookup-btn"
                  onClick={() => handleLookup()}
                  disabled={lookupLoading}
                >
                  {lookupLoading ? 'Looking up...' : 'Look up'}
                </button>
              </div>
              <div className="lc-ex-l">Try an example</div>
              <div className="lc-examples">
                <div className="lc-ex" data-testid="lookup-example-1" onClick={() => handleExampleClick('VP-M7K2P')}>VP-M7K2P</div>
                <div className="lc-ex" data-testid="lookup-example-2" onClick={() => handleExampleClick('VP-A8B9C')}>VP-A8B9C</div>
                <div className="lc-ex" data-testid="lookup-example-3" onClick={() => handleExampleClick('VP-K3M9P')}>VP-K3M9P</div>
              </div>
              <div className="tier-guide">
                <div className="tg-l">Tier reference</div>
                <div className="tg-row"><span className="tg-name t-unverified">Unverified</span><span className="tg-tag" style={{background: 'var(--tier-unverified-bg)', color: 'var(--tier-unverified)'}}>0–19</span></div>
                <div className="tg-row"><span className="tg-name" style={{color: '#6C97FF'}}>Verified</span><span className="tg-tag" style={{background: 'var(--tier-verified-bg)', color: '#6C97FF'}}>20–49</span></div>
                <div className="tg-row"><span className="tg-name t-established">Established</span><span className="tg-tag" style={{background: 'var(--tier-established-bg)', color: 'var(--tier-established)'}}>50–74</span></div>
                <div className="tg-row"><span className="tg-name t-distinguished">Distinguished</span><span className="tg-tag" style={{background: 'var(--tier-distinguished-bg)', color: 'var(--tier-distinguished)'}}>75–100</span></div>
              </div>
            </div>
            <div style={{paddingTop: '36px'}}>
              <div className="lc-result" data-testid="lookup-result">
                {lookupLoading && (
                  <div className="lr-empty">
                    <div className="lr-ic mono" style={{animation: 'pulse 1.5s infinite'}}>...</div>
                    <p className="lr-txt">Looking up VETT ID...</p>
                  </div>
                )}
                {!lookupLoading && lookupError && (
                  <div className="lr-empty">
                    <div className="lr-ic mono" style={{color: '#1B5AFF'}}>!</div>
                    <p className="lr-txt" style={{color: '#1B5AFF'}}>{lookupError}</p>
                  </div>
                )}
                {!lookupLoading && !lookupError && !lookupResult && (
                  <div className="lr-empty">
                    <div className="lr-ic mono">ID</div>
                    <p className="lr-txt">Enter a VETT ID above or tap one of the examples.</p>
                  </div>
                )}
                {!lookupLoading && lookupResult && (
                  <div className="lr-profile" data-testid="lookup-profile-result">
                    <div className="lr-header">
                      <div className="lr-avatar" style={{
                        background: lookupResult.tier === 'Distinguished' ? 'rgba(216,180,92,0.25)' : 
                                   lookupResult.tier === 'Established' ? 'rgba(0,201,167,0.2)' :
                                   lookupResult.tier === 'Verified' ? 'rgba(27,90,255,0.3)' : 'rgba(136,136,170,0.2)'
                      }}>
                        {lookupResult.first_name?.[0]}{lookupResult.last_initial}
                      </div>
                      <div className="lr-info">
                        <div className="lr-name">{lookupResult.first_name} {lookupResult.last_initial}.</div>
                        <div className="lr-headline">{lookupResult.headline || 'Professional'}</div>
                      </div>
                      <div className={`lr-tier ${getTierClass(lookupResult.tier)}`}>
                        {getTierBadge(lookupResult.tier)} {lookupResult.tier}
                      </div>
                    </div>
                    
                    <div className="lr-score-row">
                      <div className="lr-score">
                        <svg viewBox="0 0 44 44" className="lr-score-ring">
                          <circle cx="22" cy="22" r="20" fill="none" stroke="rgba(136,136,170,0.2)" strokeWidth="4"/>
                          <circle cx="22" cy="22" r="20" fill="none" stroke={
                            lookupResult.score >= 75 ? '#F0B429' :
                            lookupResult.score >= 50 ? '#00C9A7' :
                            lookupResult.score >= 20 ? '#1B5AFF' : '#5B6472'
                          } strokeWidth="4" strokeDasharray="125.6" strokeDashoffset={125.6 - (lookupResult.score / 100) * 125.6} strokeLinecap="round" transform="rotate(-90 22 22)"/>
                        </svg>
                        <span className="lr-score-num mono">{lookupResult.score}</span>
                      </div>
                      <div className="lr-score-label">
                        <div className="lr-score-title">VETT Score</div>
                        <div className="lr-score-desc">{lookupResult.records_count} verified record{lookupResult.records_count !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                    
                    {lookupResult.records && lookupResult.records.length > 0 && (
                      <div className="lr-records">
                        <div className="lr-records-title">Verified Records</div>
                        {lookupResult.records.slice(0, 3).map((record, idx) => (
                          <div className="lr-record" key={idx}>
                            <div className="lr-record-role">{record.job_title}</div>
                            <div className="lr-record-company">{record.employer_name}</div>
                            <div className="lr-record-dates">{record.start_date?.slice(0, 4)} – {record.end_date?.slice(0, 4) || 'Present'}</div>
                            {record.manager_rating && (
                              <div className="lr-record-rating">★ {record.manager_rating.toFixed(1)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <Link to={`/id/${lookupResult.vett_id}`} className="lr-view-full" data-testid="lookup-view-full">
                      View Full Profile →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="proof-section sec-pad" id="proof">
        <div className="section-glow" aria-hidden="true">
          <span className="glow-blob glow-blob-blue"></span>
          <span className="glow-blob glow-blob-teal"></span>
        </div>
        <div className="container">
          <div className="section-eyebrow light">Early access professionals</div>
          <h2 className="section-title" style={{marginBottom: '12px'}}>The feeling when it actually works.</h2>
          <p className="section-sub" style={{color: 'var(--dark-text-faint)', marginBottom: '52px'}}>Feedback from the first professionals to build verified career records and use them in real applications.</p>

          <div className="proof-grid">
            <div className="proof-card fade-up">
              <div className="pc-stars">★★★★★</div>
              <p className="pc-quote">"I sent a proposal with my VETT ID on a Monday and got a reply by Tuesday morning. The recruiter said they had already verified my record before we even spoke. First time that has happened in fifteen years of freelancing."</p>
              <div className="pc-person">
                <div className="pc-av2" style={{background: 'rgba(27,90,255,0.3)', color: '#7AA8FF'}}>MW</div>
                <div><div className="pc-pn">Marcus Webb</div><div className="pc-pt">Senior Brand Strategist · Berlin</div></div>
                <div className="pc-ptier t-distinguished">✦ Distinguished</div>
              </div>
            </div>
            <div className="proof-card fade-up">
              <div className="pc-stars">★★★★★</div>
              <p className="pc-quote">"I left a company under difficult circumstances. For two years I had no way to tell my side. The VETT record from the employer before that one speaks for itself now. The difference in how interviews go is not subtle."</p>
              <div className="pc-person">
                <div className="pc-av2" style={{background: 'rgba(0,201,167,0.2)', color: 'var(--vett-teal)'}}>SC</div>
                <div><div className="pc-pn">Sophia Chen</div><div className="pc-pt">Head of Operations · Amsterdam</div></div>
                <div className="pc-ptier t-established">● Established</div>
              </div>
            </div>
            <div className="proof-card fade-up">
              <div className="pc-stars">★★★★★</div>
              <p className="pc-quote">"I moved from Lagos to London two years ago. My entire work history was invisible here, nobody knew the companies and nobody could verify anything. My VETT records crossed with me. That passport is the first professional thing that actually transferred."</p>
              <div className="pc-person">
                <div className="pc-av2" style={{background: 'rgba(216,180,92,0.25)', color: 'var(--dark-gold-bright)'}}>YO</div>
                <div><div className="pc-pn">Yemi Okafor</div><div className="pc-pt">Product Manager · London</div></div>
                <div className="pc-ptier" style={{color: '#6C97FF'}}>✓ Verified</div>
              </div>
            </div>
            <div className="proof-card fade-up">
              <div className="pc-stars">★★★★★</div>
              <p className="pc-quote">"I have been a hiring manager for twelve years and read thousands of CVs. The first time I saw a VETT passport I understood immediately what had been missing. It is the difference between a candidate telling you who they are and a former employer confirming it."</p>
              <div className="pc-person">
                <div className="pc-av2" style={{background: 'rgba(99,102,241,0.3)', color: '#A5B4FC'}}>JP</div>
                <div><div className="pc-pn">James Park</div><div className="pc-pt">Engineering Director · Seoul</div></div>
                <div className="pc-ptier t-distinguished">✦ Distinguished</div>
              </div>
            </div>
            <div className="proof-card fade-up">
              <div className="pc-stars">★★★★★</div>
              <p className="pc-quote">"I was made redundant after five years. HR gave nothing in writing. I sent a record request directly to my line manager and he completed it the same day. That record got me my next role. I would not have been taken seriously without it."</p>
              <div className="pc-person">
                <div className="pc-av2" style={{background: 'rgba(0,201,167,0.18)', color: 'var(--vett-teal)'}}>LB</div>
                <div><div className="pc-pn">Lena Bauer</div><div className="pc-pt">Senior UX Designer · Munich</div></div>
                <div className="pc-ptier t-established">● Established</div>
              </div>
            </div>
            <div className="proof-card fade-up">
              <div className="pc-stars">★★★★★</div>
              <p className="pc-quote">"I applied to four roles after getting my first two co-signed records. Three skipped the reference check stage entirely. One recruiter told me the VETT passport gave them more useful information than any reference call ever had."</p>
              <div className="pc-person">
                <div className="pc-av2" style={{background: 'rgba(220,38,38,0.2)', color: '#FCA5A5'}}>AR</div>
                <div><div className="pc-pn">Amara Reyes</div><div className="pc-pt">Finance Manager · Dubai</div></div>
                <div className="pc-ptier" style={{color: '#6C97FF'}}>✓ Verified</div>
              </div>
            </div>
          </div>

          <div className="proof-stats">
            <p className="ps-note">Early access members across 18 countries.</p>
            <div className="ps-nums">
              <div className="ps-num"><div className="ps-nv mono">18</div><div className="ps-nl">Countries represented</div></div>
              <div className="ps-div"></div>
              <div className="ps-num"><div className="ps-nv mono">94%</div><div className="ps-nl">Record acknowledgement rate</div></div>
              <div className="ps-div"></div>
              <div className="ps-num"><div className="ps-nv mono">&lt;3%</div><div className="ps-nl">Dispute rate on published records</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section sec-pad" id="pricing">
        <div className="container">
          <div className="section-header-center">
            <div className="section-eyebrow center">Pricing</div>
            <h2 className="section-title" style={{maxWidth: '560px'}}>
              Simple, transparent, and free until your verified record does the work for you.
            </h2>
          </div>

          <div className="pricing-grid">
            <div className="price-card">
              <div className="pc-plan">Free</div>
              <div className="pc-amount">
                <span className="pc-price mono">$0</span>
                <span className="pc-period">/ forever</span>
              </div>
              <p className="pc-desc">Everything a professional needs to build a verified career record from day one. No trial, no catch. Free forever, without conditions.</p>
              <div className="pc-divider"></div>
              <div className="pc-features">
                <div className="pc-feat"><div className="pc-dot">✓</div>Permanent VETT ID in the format VP-XXXXX</div>
                <div className="pc-feat"><div className="pc-dot">✓</div>Full career passport and dual-verified employment timeline</div>
                <div className="pc-feat"><div className="pc-dot">✓</div>VETT Score, calculated from co-signed records and shown on your profile</div>
                <div className="pc-feat"><div className="pc-dot">✓</div>Unlimited record requests: manager flow, HR flow, or both</div>
                <div className="pc-feat"><div className="pc-dot">✓</div>Full dispute process, before and after publication</div>
                <div className="pc-feat"><div className="pc-dot">✓</div>Full visibility control, you decide exactly who sees what</div>
                <div className="pc-feat"><div className="pc-dot">✓</div>Profile verifiable by anyone you share your VETT ID with</div>
                <div className="pc-feat"><div className="pc-dot">✓</div>GDPR data export and deletion rights</div>
              </div>
              <Link className="pc-cta light" to="/signup">
                Claim your VETT ID, free
              </Link>
              <p className="pc-fn">No credit card. No trial period. Free forever, without conditions.</p>
            </div>

            <div className="price-card featured" data-testid="early-access-pro-card">
              <div className="pc-plan" style={{color: 'rgba(245,247,255,0.4)'}}>Early Access Pro</div>
              <div className="pc-amount">
                <span className="pc-price mono" style={{color: '#fff'}}>$0</span>
                <span className="pc-period" style={{color: 'rgba(245,247,255,0.5)'}}>/ during early access</span>
              </div>
              <div className="pc-badge" data-testid="early-access-counter">
                {earlyAccessData.isOfferActive ? (
                  <>● {earlyAccessData.remainingSlots.toLocaleString()} of 1,000 spots remaining</>
                ) : (
                  <>● Early Access closed — 1,000 professionals joined</>
                )}
              </div>
              {!earlyAccessData.isOfferActive && waitlistCount > 0 && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: 'rgba(245,247,255,0.5)',
                  fontFamily: 'var(--font-mono)'
                }} data-testid="waitlist-counter">
                  {waitlistCount.toLocaleString()} {waitlistCount === 1 ? 'person' : 'people'} on the waitlist
                </div>
              )}
              {earlyAccessData.isOfferActive && earlyAccessData.currentSignups > 0 && (
                <div className="pc-progress-bar" style={{
                  marginTop: '12px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  height: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${earlyAccessData.percentageFilled}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, var(--vett-blue), var(--vett-teal))',
                    borderRadius: '4px',
                    transition: 'width 0.5s ease-out'
                  }} />
                </div>
              )}
              <p className="pc-desc" style={{color: 'rgba(245,247,255,0.55)', marginTop: '14px'}}>
                {earlyAccessData.isOfferActive 
                  ? `Every Pro feature at no charge for the first 1,000 professionals who sign up. ${earlyAccessData.currentSignups.toLocaleString()} have already joined. Activates at $9.90 a month once the broader platform goes live, with 30 days notice before any charge.`
                  : 'Early Access is now closed. Pro features are available at $9.90/month with a 30-day free trial.'
                }
              </p>
              <div className="pc-divider" style={{background: 'rgba(255,255,255,0.1)'}}></div>
              <div className="pc-features">
                <div className="pc-feat-label">Everything in Free, plus:</div>
                <div className="pc-feat" style={{color: 'rgba(245,247,255,0.75)'}}><div className="pc-dot">✓</div>PDF passport export, a polished credential document for any application</div>
                <div className="pc-feat" style={{color: 'rgba(245,247,255,0.75)'}}><div className="pc-dot">✓</div>VETT Score history, see every movement and what drove each change</div>
                <div className="pc-feat" style={{color: 'rgba(245,247,255,0.75)'}}><div className="pc-dot">✓</div>Score improvement guidance, personalised recommendations for what raises your score</div>
                <div className="pc-feat" style={{color: 'rgba(245,247,255,0.75)'}}><div className="pc-dot">✓</div>Priority support, direct access to the team</div>
              </div>
              <Link className="pc-cta dark" to="/signup" data-testid="early-access-cta">
                Claim your Early Access Pro, free
              </Link>
              {!earlyAccessData.isOfferActive && (
                <button 
                  className="pc-cta-waitlist"
                  onClick={() => setShowWaitlistModal(true)}
                  data-testid="waitlist-trigger"
                  style={{
                    marginTop: '12px',
                    width: '100%',
                    padding: '12px 20px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Join the waitlist for next round →
                </button>
              )}
              <p className="pc-fn" style={{color: 'rgba(245,247,255,0.4)'}}>
                <strong style={{color: 'rgba(245,247,255,0.6)'}}>$9.90/month</strong> activates when the broader platform launches. You get 30 days notice before any charge. Cancel any time, your records stay permanent regardless.
              </p>
            </div>
          </div>

          <div className="pricing-ctx">
            <div className="ctx-item">
              <div className="ctx-label">Free forever</div>
              <div className="ctx-val">$0</div>
              <div className="ctx-sub">A fully verified career record, yours permanently</div>
            </div>
            <div className="ctx-div"></div>
            <div className="ctx-item">
              <div className="ctx-label">Pro when it activates</div>
              <div className="ctx-val teal">$9.90</div>
              <div className="ctx-sub">Per month: PDF export, score history, guidance</div>
            </div>
            <div className="ctx-div"></div>
            <div className="ctx-item">
              <div className="ctx-label">Notice before any charge</div>
              <div className="ctx-val">30 days</div>
              <div className="ctx-sub">We tell you before we bill you. Always.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="sec-pad" id="faq">
        <div className="container">
          <div className="section-header-center">
            <div className="section-eyebrow center">Frequently asked</div>
            <h2 className="section-title">Every question, answered plainly.</h2>
          </div>

          <div className="faq-wrap">
            <div className="faq-item">
              <button className="faq-q" type="button" onClick={handleFaqToggle}>
                What exactly is VETT Pro and what do I actually get?
                <span className="faq-tog">+</span>
              </button>
              <div className="faq-a">
                <p>You get a permanent professional ID, your VETT ID in the format VP-XXXXX, and a career passport that accumulates co-signed employment records. Each record starts with you: you pre-fill your role, dates, responsibilities, and achievements. Two separate contacts are then invited, your direct manager, who reviews your work and adds a professional assessment, and HR, who confirms the institutional facts including your official title, employment dates, and departure reason. They complete their sections independently and in parallel, without seeing each other's input. You then receive the combined record, review every field, dispute anything inaccurate, and co-sign. Once every party has signed, the record locks permanently and neither side can alter it.</p>
              </div>
            </div>
            <div className="faq-item">
              <button className="faq-q" type="button" onClick={handleFaqToggle}>
                How is this different from LinkedIn?
                <span className="faq-tog">+</span>
              </button>
              <div className="faq-a">
                <p>LinkedIn is a self-reported, engagement-optimised network. Your profile says what you say it says. Endorsements are one-click gestures with no verification behind them. Recommendations are written by the person giving them, unstructured, and revocable at any time. VETT Pro has no engagement mechanics. Every record is authored by the employer, reviewed by you, and co-signed by both parties before publication, and once published neither party can alter it. LinkedIn is built for self-presentation. VETT Pro is built for verified truth. They solve different problems, and VETT Pro provides what LinkedIn was never designed to deliver.</p>
              </div>
            </div>
            <div className="faq-item">
              <button className="faq-q" type="button" onClick={handleFaqToggle}>
                Is it genuinely free forever?
                <span className="faq-tog">+</span>
              </button>
              <div className="faq-a">
                <p>Yes. Your VETT ID, your passport, your career timeline, unlimited record requests, your VETT Score, full visibility controls, and the complete dispute process are all free, permanently. No trial, no card required. The free tier is a complete working product, not a preview designed to frustrate you into upgrading. Early Access Pro is currently free too and adds PDF passport export, VETT Score history, score improvement guidance, and priority support. Pro activates at $9.90 a month once the broader platform launches, with 30 days notice before any charge. If you never upgrade, your VETT ID stays yours and your passport stays active for life.</p>
              </div>
            </div>
            <div className="faq-item">
              <button className="faq-q" type="button" onClick={handleFaqToggle}>
                What if my employer writes something I believe is inaccurate?
                <span className="faq-tog">+</span>
              </button>
              <div className="faq-a">
                <p>Before any record is published you review every field. If you believe something is wrong, you open a formal dispute, selecting the specific fields you are contesting and providing your written account. The employer has 14 days to respond. A VETT administrator reviews the dispute independently. If the record contains inaccurate information, the employer corrects it before publication. If the dispute is not upheld, the record returns to you for acknowledgement, but you are never compelled to co-sign something you believe is false: a record cannot be published without your signature. Even after publication you can open a dispute, and your account becomes permanently attached to the record.</p>
              </div>
            </div>
            <div className="faq-item">
              <button className="faq-q" type="button" onClick={handleFaqToggle}>
                What if a past employer ignores my record request?
                <span className="faq-tog">+</span>
              </button>
              <div className="faq-a">
                <p>If a past employer ignores your request, your passport shows a "request sent, awaiting response" entry with the date it was sent. Future employers can see that you attempted to build a verified record and that the employer chose not to engage. Where employees have a legal right to a formal employment reference, VETT Pro's request process is designed to support that entitlement. VETT Pro also sends follow-up reminders to employers on your behalf, and the process requires nothing more than a work email address on their end.</p>
              </div>
            </div>
            <div className="faq-item">
              <button className="faq-q" type="button" onClick={handleFaqToggle}>
                Does my past employer need a VETT account?
                <span className="faq-tog">+</span>
              </button>
              <div className="faq-a">
                <p>No. Both your manager and HR receive a secure link by email and complete their sections in a browser, no account, no software, no IT approval, no subscription. Your manager's section takes about 10 minutes. HR's section takes about 5 minutes. Both are mobile-friendly and work from any device. If your manager has moved on from the company, their personal email address works perfectly, the link is not domain-restricted.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="section-glow" aria-hidden="true">
          <span className="glow-blob glow-blob-blue"></span>
          <span className="glow-blob glow-blob-teal"></span>
        </div>
        <h2 className="final-headline">
          Your career is not<br/><em>their story to tell.</em>
        </h2>
        <p className="final-sub">
          You spent years building something real. VETT Pro makes sure it stays permanently on record, co-signed by the people who saw it, owned by you for life.
        </p>
        <div className="final-ctas">
          <Link className="btn-hero-primary" to="/signup">
            Claim Your VETT ID, Free
          </Link>
          <a className="btn-hero-ghost" href="#faq">
            Read the FAQ
          </a>
        </div>
        <p className="final-footnote">Free forever. No credit card. Two minutes to claim.</p>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-top">
          <div>
            <div className="footer-logo">VETT Pro</div>
            <p className="footer-blurb">Verified employment infrastructure for professionals. Dual-signed records, a portable VETT ID, and a score built by the people who worked with you.</p>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Product</div>
            <a href="#how">How it works</a>
            <a href="#score">Your score</a>
            <a href="#verify">Verify a record</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Company</div>
            <Link to="/about">About</Link>
            <Link to="/careers">Careers</Link>
            <Link to="/contact">Contact</Link>
            <a href="#pricing">Early access programme</a>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Legal</div>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms of service</Link>
            <Link to="/gdpr">GDPR and DPA</Link>
            <Link to="/impressum">Impressum</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">© 2026 VETT Group. Erkelenz, Germany.</div>
          <div className="footer-copy">contact@getvett.org, vettpro.org</div>
        </div>
      </footer>

      {/* Score Explainer Modal */}
      <ScoreExplainerModal 
        isOpen={showScoreExplainer} 
        onClose={() => setShowScoreExplainer(false)} 
      />

      {/* Waitlist Modal (when Early Access is full) */}
      {showWaitlistModal && (
        <div className="waitlist-modal-overlay" onClick={() => setShowWaitlistModal(false)} data-testid="waitlist-modal-overlay">
          <div className="waitlist-modal" onClick={e => e.stopPropagation()} data-testid="waitlist-modal">
            <button 
              className="waitlist-close" 
              onClick={() => setShowWaitlistModal(false)}
              aria-label="Close waitlist modal"
            >
              ×
            </button>
            {!waitlistSuccess ? (
              <>
                <div className="waitlist-icon">📬</div>
                <h3 className="waitlist-title">Join the Waitlist</h3>
                <p className="waitlist-desc">
                  Early Access is full! Enter your email to be notified when new spots open 
                  or when we expand the programme.
                </p>
                <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                  <input
                    type="email"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    className="waitlist-input"
                    data-testid="waitlist-email-input"
                  />
                  <button 
                    type="submit" 
                    className="waitlist-submit"
                    disabled={waitlistSubmitting}
                    data-testid="waitlist-submit-btn"
                  >
                    {waitlistSubmitting ? 'Joining...' : 'Notify Me'}
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="waitlist-icon">✓</div>
                <h3 className="waitlist-title">You are on the list!</h3>
                <p className="waitlist-desc">
                  We will email you as soon as a spot opens up. In the meantime, you can still 
                  create your free VETT ID and start building your record history.
                </p>
                <Link to="/signup" className="waitlist-cta" data-testid="waitlist-signup-link">
                  Get your free VETT ID →
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VettProLandingPage;
